import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, getUser } from "../api";
import { toast } from "sonner";
import { ArrowLeft, Download, Users, MonitorSmartphone, Wifi, UploadCloud, Send } from "lucide-react";

const ROLE_LBL = { user: "Utente", professional: "Professionista", organization: "Struttura", admin: "Admin" };
const fmtDate = (s) => (s ? s.slice(0, 16).replace("T", " ") : "—");
const fmtSize = (b) => (b ? `${(b / 1048576).toFixed(1)} MB` : "—");

const Counter = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="yp-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide"><Icon size={15} />{label}</div>
    <div className="text-3xl font-bold mt-1 text-slate-900">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>);

export default function AdminPamConnect() {
  const u = getUser();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [installs, setInstalls] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [releases, setReleases] = useState([]);
  const [busy, setBusy] = useState(false);
  const [upFile, setUpFile] = useState(null);
  const [upVersion, setUpVersion] = useState("");
  const [upExpected, setUpExpected] = useState("");
  const [upResult, setUpResult] = useState(null);
  const [notifVersion, setNotifVersion] = useState("");
  const [preview, setPreview] = useState(null);
  const [notifResult, setNotifResult] = useState(null);

  const loadStats = () => api.get("/admin/pam-connect/stats").then(({ data }) => setStats(data)).catch(() => {});
  const loadInstalls = () => api.get(`/admin/pam-connect/installations${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setInstalls(data.items || [])).catch(() => {});
  const loadReleases = () => api.get("/admin/pam-connect/releases").then(({ data }) => setReleases(data.items || [])).catch(() => {});

  useEffect(() => { if (u?.role !== "admin") return;
    if (tab === "dashboard") loadStats();
    if (tab === "installs") loadInstalls();
    if (tab === "updates" || tab === "history") loadReleases();
  }, [tab]); // eslint-disable-line

  if (!u) return <Navigate to="/login" replace />;
  if (u.role !== "admin") return <Navigate to="/" replace />;

  const doUpload = async () => {
    if (!upFile || !upVersion.trim()) { toast.error("Seleziona file e versione"); return; }
    setBusy(true); setUpResult(null);
    try {
      const fd = new FormData();
      fd.append("file", upFile); fd.append("version", upVersion.trim());
      if (upExpected.trim()) fd.append("expected_sha256", upExpected.trim());
      const { data } = await api.post("/admin/pam-connect/releases/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpResult(data); toast.success("Candidate caricata (non pubblicata)"); loadReleases();
    } catch (e) {
      toast.error(e.response?.data?.detail === "invalid_installer" ? "File non valido (.exe richiesto)" : "Errore caricamento");
    } finally { setBusy(false); }
  };

  const doPublish = async (rid, version) => {
    if (!window.confirm(`Pubblicare la versione ${version} sul canale pubblico? Sostituirà la versione attualmente distribuita.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/pam-connect/releases/${rid}/publish`, { confirm: true });
      toast.success(`Versione ${version} pubblicata`); loadReleases(); loadStats();
    } catch (e) { toast.error("Errore pubblicazione: " + (e.response?.data?.detail || "")); }
    finally { setBusy(false); }
  };

  const doPreview = async () => {
    if (!notifVersion.trim()) { toast.error("Indica la versione pubblicata"); return; }
    setBusy(true); setPreview(null); setNotifResult(null);
    try {
      const { data } = await api.post("/admin/pam-connect/announce", { version: notifVersion.trim(), dry_run: true });
      setPreview(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? `Versione non pubblicata (pubblica: ${d.published_version})` : "Errore anteprima");
    } finally { setBusy(false); }
  };

  const doSend = async () => {
    if (!preview) return;
    if (!window.confirm(`Inviare la notifica per la versione ${preview.version} a ${preview.inapp_pending} account in-app e ${preview.email_pending} email?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pam-connect/announce", { version: preview.version, dry_run: false, confirm: true });
      setNotifResult(data); toast.success("Invio completato"); loadReleases();
    } catch (e) { toast.error("Errore invio"); }
    finally { setBusy(false); }
  };

  const TABS = { dashboard: "Dashboard", installs: "Utenti e installazioni", updates: "Gestione aggiornamenti", notify: "Invia notifica", history: "Storico" };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="admin-pamc">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="pamc-back"><ArrowLeft size={15} />Admin</Link>
      <h1 className="text-2xl font-bold mb-1">PAM Connect</h1>
      <p className="text-sm text-slate-500 mb-5">Gestione release, pubblicazione, notifiche e statistiche.</p>

      <div className="flex gap-2 mb-6 flex-wrap">
        {Object.entries(TABS).map(([k, label]) => (
          <button key={k} className={`yp-btn ${tab === k ? "yp-btn-primary" : "yp-btn-ghost"} !py-1 !px-3 text-sm`} onClick={() => { setTab(k); setDetail(null); }} data-testid={`pamc-tab-${k}`}>{label}</button>))}
      </div>

      {tab === "dashboard" && stats && (<div data-testid="pamc-dashboard">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Counter icon={Download} label="Download totali" value={stats.downloads_total} sub={Object.entries(stats.downloads_by_version || {}).map(([v, c]) => `${v}: ${c}`).join(" · ") || null} testid="pamc-kpi-downloads" />
          <Counter icon={Users} label="Account che hanno scaricato" value={stats.unique_downloaders} testid="pamc-kpi-users" />
          <Counter icon={MonitorSmartphone} label="Installazioni registrate" value={stats.installations_registered} sub="Enrollment completati (non revocati)" testid="pamc-kpi-installs" />
          <Counter icon={Wifi} label="Connettori online" value={stats.connectors_online} sub={`Heartbeat < ${stats.online_window_seconds}s`} testid="pamc-kpi-online" />
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Tracciamento download disponibile da: {stats.tracking_since ? fmtDate(stats.tracking_since) : "nessun download tracciato finora"}. Un download non implica l'installazione sul dispositivo.</p>
      </div>)}

      {tab === "installs" && (<div data-testid="pamc-installs">
        <form onSubmit={(e) => { e.preventDefault(); loadInstalls(); }} className="flex gap-2 mb-4 max-w-md">
          <input className="yp-input !py-1.5 text-sm flex-1" placeholder="Cerca nome o email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pamc-search" />
          <button className="yp-btn yp-btn-primary !py-1.5 !px-3 text-sm" data-testid="pamc-search-btn">Cerca</button>
        </form>
        <div className="yp-card overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="p-2">Account</th><th className="p-2">Categoria</th><th className="p-2">Email</th>
              <th className="p-2">Download</th><th className="p-2">Install.</th><th className="p-2">Online</th><th className="p-2">Offline</th>
              <th className="p-2">Versioni</th><th className="p-2">Ultima conn.</th></tr></thead>
            <tbody>
              {installs.map((r) => (
                <tr key={r.account_id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => api.get(`/admin/pam-connect/installations/${r.account_id}`).then(({ data }) => setDetail({ ...data, name: r.name })).catch(() => {})} data-testid="pamc-install-row">
                  <td className="p-2 font-medium">{r.name}</td><td className="p-2">{ROLE_LBL[r.role] || r.role}</td><td className="p-2 text-slate-500">{r.email}</td>
                  <td className="p-2">{r.downloads}</td><td className="p-2">{r.installs_registered}</td>
                  <td className="p-2 text-emerald-600 font-semibold">{r.installs_online}</td><td className="p-2 text-slate-400">{r.installs_offline}</td>
                  <td className="p-2 text-xs">{(r.versions || []).join(", ") || "—"}</td><td className="p-2 text-xs">{fmtDate(r.last_seen_at)}</td></tr>))}
              {!installs.length && <tr><td colSpan={9} className="p-4 text-center text-slate-400">Nessun risultato</td></tr>}
            </tbody>
          </table>
        </div>
        {detail && (<div className="yp-card p-4 mt-4" data-testid="pamc-install-detail">
          <div className="font-semibold mb-2">{detail.name} — connettori</div>
          <div className="space-y-2">
            {detail.connectors.map((c) => (
              <div key={c.agent_id} className="flex items-center justify-between text-sm border-b border-slate-50 pb-1.5">
                <div><span className="font-medium">{c.name || "—"}</span> <span className="text-xs text-slate-500">v{c.version || "?"}</span>{c.status === "revoked" && <span className="ml-2 text-[10px] uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">Revocato</span>}</div>
                <div className="text-xs text-slate-500 flex items-center gap-3">
                  <span className={c.online ? "text-emerald-600 font-semibold" : "text-slate-400"}>{c.status === "revoked" ? "—" : c.online ? "Online" : "Offline"}</span>
                  <span>Reg: {fmtDate(c.registered_at)}</span><span>HB: {fmtDate(c.last_seen_at)}</span></div>
              </div>))}
          </div>
        </div>)}
      </div>)}

      {tab === "updates" && (<div className="space-y-6" data-testid="pamc-updates">
        <div className="yp-card p-5 max-w-xl">
          <div className="font-semibold flex items-center gap-2 mb-3"><UploadCloud size={17} />1 · Carica nuova versione</div>
          <input type="file" accept=".exe" onChange={(e) => setUpFile(e.target.files?.[0] || null)} className="block text-sm mb-2" data-testid="pamc-up-file" />
          <input className="yp-input !py-1.5 text-sm w-full mb-2" placeholder="Versione (es. 1.0.9)" value={upVersion} onChange={(e) => setUpVersion(e.target.value)} data-testid="pamc-up-version" />
          <input className="yp-input !py-1.5 text-sm w-full mb-3" placeholder="SHA-256 atteso (opzionale)" value={upExpected} onChange={(e) => setUpExpected(e.target.value)} data-testid="pamc-up-expected" />
          <button className="yp-btn yp-btn-primary !py-1.5 text-sm" disabled={busy} onClick={doUpload} data-testid="pamc-up-submit">Carica candidate</button>
          {upResult && (<div className="mt-3 text-xs bg-slate-50 rounded-lg p-3 break-all" data-testid="pamc-up-result">
            <div>File: {upResult.filename} · {fmtSize(upResult.size)}</div><div>SHA-256: {upResult.sha256}</div>
            {upResult.expected_match != null && <div className={upResult.expected_match ? "text-emerald-600" : "text-rose-600"}>SHA-256 atteso: {upResult.expected_match ? "corrispondente" : "NON corrispondente"}</div>}
            <div>Stato: {upResult.status} (non pubblicata)</div></div>)}
        </div>
        <div className="yp-card p-5">
          <div className="font-semibold mb-3">2 · Pubblica aggiornamento</div>
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100"><th className="p-2">Versione</th><th className="p-2">Stato</th><th className="p-2">Dim.</th><th className="p-2">SHA-256</th><th className="p-2">Caricata</th><th className="p-2"></th></tr></thead>
            <tbody>{releases.map((r) => (<tr key={r.id || r.version} className="border-b border-slate-50">
              <td className="p-2 font-medium">{r.version}</td>
              <td className="p-2"><span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${r.status === "published" ? "bg-emerald-100 text-emerald-700" : r.status === "candidate" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{r.status}</span></td>
              <td className="p-2 text-xs">{fmtSize(r.size)}</td><td className="p-2 text-[10px] break-all max-w-[160px]">{r.sha256?.slice(0, 16)}…</td><td className="p-2 text-xs">{fmtDate(r.uploaded_at)}</td>
              <td className="p-2">{r.status === "candidate" && r.id && <button className="yp-btn yp-btn-primary !py-1 !px-2.5 text-xs" disabled={busy} onClick={() => doPublish(r.id, r.version)} data-testid="pamc-publish-btn">Pubblica</button>}</td></tr>))}</tbody>
          </table></div>
        </div>
      </div>)}

      {tab === "notify" && (<div className="yp-card p-5 max-w-xl space-y-3" data-testid="pamc-notify">
        <div className="font-semibold flex items-center gap-2"><Send size={17} />3 · Invia notifica aggiornamento</div>
        <p className="text-xs text-slate-500">L'invio è consentito solo per una versione realmente pubblicata. Nessun invio automatico: l'anteprima non invia nulla.</p>
        <div className="flex gap-2">
          <input className="yp-input !py-1.5 text-sm flex-1" placeholder="Versione pubblicata (es. 1.0.6)" value={notifVersion} onChange={(e) => setNotifVersion(e.target.value)} data-testid="pamc-notif-version" />
          <button className="yp-btn yp-btn-outline !py-1.5 !px-3 text-sm" disabled={busy} onClick={doPreview} data-testid="pamc-notif-preview">Anteprima destinatari</button>
        </div>
        {preview && (<div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1.5" data-testid="pamc-notif-dryrun">
          <div>Versione: <b>{preview.version}</b></div>
          <div>Account destinatari: <b>{preview.recipients_total}</b></div>
          <div>Già notificati (in-app): {preview.inapp_already_sent} · Email già inviate: {preview.email_already_sent}</div>
          <div>Da inviare: <b>{preview.inapp_pending}</b> in-app · <b>{preview.email_pending}</b> email</div>
          <div className="mt-2 border-t border-slate-200 pt-2 text-xs">
            <div className="font-semibold">Anteprima testi</div>
            <div className="mt-1"><b>{preview.preview?.inapp_title}</b><br />{preview.preview?.inapp_text}</div>
            <div className="mt-1 text-slate-500">Email: {preview.preview?.email_subject}</div>
            <div className="text-slate-500">Link: {preview.preview?.url}</div>
          </div>
          <button className="yp-btn yp-btn-primary !py-1.5 text-sm mt-2" disabled={busy || (preview.inapp_pending === 0 && preview.email_pending === 0)} onClick={doSend} data-testid="pamc-notif-send">Invia notifica aggiornamento</button>
        </div>)}
        {notifResult && (<div className="bg-emerald-50 rounded-lg p-3 text-sm" data-testid="pamc-notif-result">
          In-app inviate: {notifResult.inapp_sent} (skip {notifResult.inapp_skipped}) · Email inviate: {notifResult.email_sent} (skip {notifResult.email_skipped}, fallite {notifResult.email_failed})
        </div>)}
      </div>)}

      {tab === "history" && (<div className="yp-card overflow-x-auto" data-testid="pamc-history">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
            <th className="p-2">Versione</th><th className="p-2">Stato</th><th className="p-2">Caricata</th><th className="p-2">Pubblicata</th>
            <th className="p-2">Download</th><th className="p-2">In-app</th><th className="p-2">Email</th><th className="p-2">Falliti</th></tr></thead>
          <tbody>{releases.map((r) => (<tr key={r.id || r.version} className="border-b border-slate-50">
            <td className="p-2 font-medium">{r.version}</td><td className="p-2 text-xs">{r.status}</td><td className="p-2 text-xs">{fmtDate(r.uploaded_at)}</td><td className="p-2 text-xs">{fmtDate(r.published_at)}</td>
            <td className="p-2">{r.downloads}</td><td className="p-2">{r.notices_inapp}</td><td className="p-2">{r.notices_email}</td><td className="p-2 text-rose-500">{r.notices_failed}</td></tr>))}</tbody>
        </table>
      </div>)}
    </div>);
}
